// Tooltip.tsx
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Side = "top" | "bottom" | "left" | "right";

type TooltipProps = {
  text: string;
  side?: Side;
  children: React.ReactNode;
  offset?: number;     // px gap between trigger and tooltip
  delayIn?: number;    // ms
  delayOut?: number;   // ms
  maxWidth?: number;   // px
};

export const Tooltip: React.FC<TooltipProps> = ({
  text,
  side = "top",
  children,
  offset = 10,
  delayIn = 70,
  delayOut = 60,
  maxWidth = 680,
}) => {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{top: number; left: number} | null>(null);

  // timers for smooth open/close
  const openT = useRef<number | null>(null);
  const closeT = useRef<number | null>(null);

  const show = () => {
    if (closeT.current) window.clearTimeout(closeT.current);
    openT.current = window.setTimeout(() => setOpen(true), delayIn);
  };
  const hide = () => {
    if (openT.current) window.clearTimeout(openT.current);
    closeT.current = window.setTimeout(() => setOpen(false), delayOut);
  };

  // Compute position whenever open, on resize/scroll
  const position = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    let top = 0, left = 0;
    if (side === "top") {
      top = r.top + window.scrollY - offset;
      left = r.left + window.scrollX + r.width / 2;
    } else if (side === "bottom") {
      top = r.bottom + window.scrollY + offset;
      left = r.left + window.scrollX + r.width / 2;
    } else if (side === "left") {
      top = r.top + window.scrollY + r.height / 2;
      left = r.left + window.scrollX - offset;
    } else {
      // right
      top = r.top + window.scrollY + r.height / 2;
      left = r.right + window.scrollX + offset;
    }
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    position();
    const onScroll = () => position();
    const onResize = () => position();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, offset]);

  useEffect(() => () => {
    if (openT.current) window.clearTimeout(openT.current);
    if (closeT.current) window.clearTimeout(closeT.current);
  }, []);

  // Arrow styles per side
  const arrowCommon = "absolute w-2.5 h-2.5 rotate-45 bg-slate-900/95 ring-1 ring-white/10";
  const arrowStyle =
    side === "top"    ? { bottom: -5, left: "50%", transform: "translateX(-50%)" } :
    side === "bottom" ? { top: -5,    left: "50%", transform: "translateX(-50%)" } :
    side === "left"   ? { right: -5,  top: "50%",  transform: "translateY(-50%)" } :
                        { left: -5,   top: "50%",  transform: "translateY(-50%)" };

  // Translate to center/edge
  const translate =
    side === "top" || side === "bottom"
      ? " -translate-x-1/2"
      : side === "left"
      ? " -translate-y-1/2"
      : " -translate-y-1/2";

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex items-center"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onTouchStart={() => setOpen(v => !v)}
      aria-describedby={id}
    >
      {children}

      {open && coords &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              // fixed stacking outside app layers
            }}
            className={[
              "pointer-events-none z-[9999] absolute",
              // translate
              side === "top"    ? "-translate-y-full" + translate :
              side === "bottom" ? "" + translate :
              side === "left"   ? "-translate-x-full" + translate :
                                   "" + translate,
              // container look
              "rounded-2xl bg-slate-900/95 text-white/95 ring-1 ring-white/10",
              "shadow-2xl backdrop-blur-sm",
              // text
              "text-sm font-medium leading-snug tracking-normal font-hebrew antialiased",
              "px-3.5 py-2",
              "max-w-[80vw]",
              `md:max-w-[${maxWidth}px]`,
              // animation
              "opacity-0 data-[show=true]:opacity-100 transition-opacity duration-120",
            ].join(" ")}
            data-show={open}
          >
            <div className="whitespace-normal">
              {text}
            </div>
            <div
              className={arrowCommon}
              style={arrowStyle as React.CSSProperties}
              aria-hidden
            />
          </div>,
          document.body
        )
      }
    </span>
  );
};
